import { useRequiredContext } from "@redotech/react-util/context";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { Flex } from "../flex";
import * as styles from "./setting-section-tab-nav.module.css";
import { TabNavigationContext } from "./tab-navigation-provider";

/** Nearest ancestor that actually scrolls, so we can listen for its scroll. */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function SettingSectionTabNav({
  rootMargin = "-200px 0px -50% 0px",
  activationOffset = 24,
}: {
  /** Band within the viewport that counts as "current". */
  rootMargin?: string;
  /**
   * Distance from the top of the viewport that marks the "current" line. The
   * active section is the last one scrolled above it, so short panes (where no
   * section ever reaches a narrow band) still track correctly.
   */
  activationOffset?: number;
} = {}) {
  const { sectionRefs, sectionErrors } =
    useRequiredContext(TabNavigationContext);
  const [activeSection, setActiveSection] = useState<string>("");

  const eleList = useMemo(() => {
    return Object.entries(sectionRefs)
      .filter(([, { ref }]) => ref !== null)
      .sort(([, { ref: refA }], [, { ref: refB }]) => {
        if (!refA || !refB) {
          return 0;
        }
        const topA = refA.getBoundingClientRect().top + window.scrollY;
        const topB = refB.getBoundingClientRect().top + window.scrollY;
        return topA - topB;
      });
  }, [sectionRefs]);

  useEffect(() => {
    const firstRef = Object.values(sectionRefs).find(({ ref }) => ref)?.ref;
    const scroller = firstRef ? findScrollParent(firstRef) : null;

    // Active section = the last one whose top has scrolled above the activation
    // line, measured from the top of whatever actually scrolls (the pane, or
    // the window). Always resolves, so it never sticks on the first section in
    // a short pane the way a narrow intersection band did.
    const computeActive = () => {
      const line =
        (scroller ? scroller.getBoundingClientRect().top : 0) +
        activationOffset;
      const sorted = Object.entries(sectionRefs)
        .filter(([, { ref }]) => ref !== null)
        .sort(
          ([, a], [, b]) =>
            a.ref!.getBoundingClientRect().top -
            b.ref!.getBoundingClientRect().top,
        );
      if (sorted.length === 0) {
        return;
      }
      let activeId = sorted[0]![0];
      for (const [id, { ref }] of sorted) {
        if (ref!.getBoundingClientRect().top <= line) {
          activeId = id;
        } else {
          break;
        }
      }

      setActiveSection(activeId);
    };

    computeActive();

    const observer = new IntersectionObserver(computeActive, {
      threshold: [0, 1],
      rootMargin,
    });
    for (const { ref } of Object.values(sectionRefs)) {
      if (ref) {
        observer.observe(ref);
      }
    }

    scroller?.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("scroll", computeActive, {
      passive: true,
      capture: true,
    });

    return () => {
      observer.disconnect();
      scroller?.removeEventListener("scroll", computeActive);
      window.removeEventListener("scroll", computeActive, { capture: true });
    };
  }, [sectionRefs, rootMargin, activationOffset]);

  return (
    <div className={styles.sideNavContainer}>
      <Flex dir="column" gap="none">
        {eleList.map(([id, { title, ref }]) => (
          <SettingSectionTabNavItem
            active={activeSection === id}
            elementRef={ref}
            hasError={(sectionErrors[id]?.length ?? 0) > 0}
            id={id}
            key={id}
            title={title}
          />
        ))}
      </Flex>
    </div>
  );
}

function SettingSectionTabNavItem({
  title,
  elementRef,
  active,
  hasError,
}: {
  title: string;
  id: string;
  elementRef: HTMLElement | null;
  active: boolean;
  hasError: boolean;
}) {
  const scrollToElement = () => {
    if (!elementRef) {
      return;
    }
    elementRef.scrollIntoView({ behavior: "smooth", block: "start" });
    const sectionFlash = styles.sectionFlash;
    if (!sectionFlash) {
      return;
    }
    // Re-trigger the animation even if the same item is clicked twice.
    elementRef.classList.remove(sectionFlash);
    void elementRef.offsetWidth;
    elementRef.classList.add(sectionFlash);
    window.setTimeout(() => elementRef.classList.remove(sectionFlash), 900);
  };
  return (
    <Flex
      align="center"
      className={classNames(styles.sideNavLink, active && styles.active)}
      gap="xs"
      onClick={scrollToElement}
    >
      {title}
      {hasError && <span className={styles.errorDot} />}
    </Flex>
  );
}
